import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteExtExclusion,
    getExtExclusion,
    getExtExclusions,
    setExtExclusion,
    updateExtExclusionsOrder,
    setOpenModal,
    setFilters,
} from '../../store/extExclusions/actions';
import constants from '../../commonUtils/constants';
import IconButton from '../../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const ExtExclusionsContext = createContext();

export const useExtExclusions = () => useContext(ExtExclusionsContext);

export const ExtExclusionsProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { filters, ExtExclusionsList, loading, errors, openModal } = useSelector((state) => state.ExtExclusions);
    const { extractionViewOnly, extractionData } = useSelector((state) => state.Extractions);
    const { extVersion } = useSelector((state) => state.ExtVersions);
    const versionId = extVersion?.id || extVersion?._id;

    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [localExtExclusions, setLocalExtExclusions] = useState([]);

    const debouncedGetExtExclusions = useMemo(() => debounce((params) => {
        dispatch(getExtExclusions(params));
    }, 300), [dispatch]);

    // Sync versionId and is_draft into Redux filters whenever scope changes
    useEffect(() => {
        if (!extractionData?.id) return;

        const updatedFilters = {
            ...filters,
            is_draft: extractionViewOnly && versionId ? false : true,
            versionId: extractionViewOnly && versionId ? versionId : null,
        };

        dispatch(setFilters(updatedFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractionViewOnly, versionId, extractionData?.id]);

    // Fetch whenever filters change (filters now includes versionId + is_draft)
    useEffect(() => {
        if (!extractionData?.id) return;
        debouncedGetExtExclusions({ extId: extractionData.id, ...filters });
    }, [filters, extractionData?.id, debouncedGetExtExclusions]);

    const extExclusions = useMemo(() => {
        return ExtExclusionsList?.data?.map(
            (item, index) => ({
                id: `${item._id}_${index}`,
                _id: item._id,
                key: item.key,
                title: item.title,
                query: item.query,
                fields: item.fields,
                originalIndex: index + 1,
            })
        ) || [];
    }, [ExtExclusionsList]);

    useEffect(() => {
        setLocalExtExclusions([]);
    }, [ExtExclusionsList?.data]);

    const displayData = localExtExclusions.length > 0 ? localExtExclusions : extExclusions;

    const pagination = useMemo(() => ExtExclusionsList?.pagination, [ExtExclusionsList]);

    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetExtExclusions({ extId: extractionData?.id, currentPage, perPage, ...filters });
    }, [filters, debouncedGetExtExclusions, extractionData?.id]);

    const handleRowReorder = useCallback((reorderedData) => {
        const updatedData = reorderedData.map((item, index) => ({
            ...item,
            priority: index + 1,
        }));

        setLocalExtExclusions(updatedData);

        const orderData = updatedData.map((item, index) => ({
            _id: item._id,
            priority: index + 1
        }));

        dispatch(updateExtExclusionsOrder(orderData));
    }, [dispatch]);

    const getRowId = useCallback((row) => {
        return row.id?.toString() || row._id?.toString() || '';
    }, []);

    const editExtExclusion = useCallback((id) => {
        dispatch(setExtExclusion(null));
        dispatch(setOpenModal(true));
        dispatch(getExtExclusion(id));
    }, [dispatch]);

    const openAddModal = useCallback(() => {
        dispatch(setExtExclusion(null));
        dispatch(setOpenModal(true));
    }, [dispatch]);

    const handleCloseModal = useCallback((action) => {
        if (action === 'delete') {
            setShowDeleteConfirmationDialog(false);
            setToDeleteId("");
        } else {
            dispatch(setOpenModal(false));
            dispatch(setExtExclusion(null));
        }
    }, [dispatch]);

    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const handleDelete = useCallback(() => {
        dispatch(deleteExtExclusion(toDeleteId));
        setToDeleteId("");
        setShowDeleteConfirmationDialog(false);
    }, [dispatch, toDeleteId]);

    const resetOrder = useCallback(() => {
        setLocalExtExclusions([]);
    }, []);

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.accessor('originalIndex', { header: '#', cell: info => info.row.index + 1 }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    <IconButton
                        disabled={extractionViewOnly}
                        variant="action_default"
                        size="icon"
                        Icon={PencilSquareIcon}
                        onClick={() => editExtExclusion(info.row.original._id)}
                    />
                    <IconButton
                        disabled={extractionViewOnly}
                        variant="action_default"
                        size="icon"
                        Icon={TrashIcon}
                        onClick={() => askDeleteConfirmation(info.row.original._id)}
                    />
                </>
            )
        }),
    ], [askDeleteConfirmation, columnHelper, editExtExclusion, extractionViewOnly]);

    const table = useReactTable({
        data: displayData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowId: getRowId,
        initialState: {
            pagination: {
                pageIndex: (ExtExclusionsList?.pagination?.currentPage || 1) - 1,
                pageSize: ExtExclusionsList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            },
        },
        manualPagination: true,
        pageCount: ExtExclusionsList?.pagination?.totalPages,
    });

    return (
        <ExtExclusionsContext.Provider value={{
            extExclusions: displayData,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editExtExclusion,
            openAddModal,
            openModal,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
            handleRowReorder,
            getRowId,
            resetOrder,
        }}>
            {children}
        </ExtExclusionsContext.Provider>
    );
};